import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 30_000; // Beckn TTL PT30S — show "taking longer" past this.

/**
 * Generic poller for an async Beckn draft (metro tickets or on-demand rides).
 * Polls the draft for a transaction every 1.5s, stopping when the current step
 * completes or errors. Surfaces a `timedOut` flag when no callback has arrived
 * within ~30s. A booking reuses one txn across steps, so each step (signalled by
 * `step` changing) gets its own ~30s budget.
 *
 * Both `TransitDraft` and `RideDraft` share the exact `step`/`status` contract,
 * so metro and rides each wrap this with their own draftFn + queryKey factory.
 */
export function useBeckonDraft<T extends { step: string; status: string }>(
  transactionId: string | null,
  draftFn: (txnId: string) => Promise<T>,
  queryKey: (txnId: string) => readonly unknown[]
) {
  const [timedOut, setTimedOut] = useState(false);
  const startRef = useRef<number | null>(null);

  const query = useQuery({
    queryKey: queryKey(transactionId ?? ''),
    queryFn: () => draftFn(transactionId as string),
    enabled: Boolean(transactionId) && !timedOut,
    refetchInterval: (q) => {
      const d = q.state.data;
      // 'unavailable' is terminal too: a city-only discovery whose search TTL elapsed with no serving
      // BPP (nobody serves the city / every responder was dropped). Stop polling — there is nothing more to wait for.
      if (d && (d.status === 'completed' || d.status === 'error' || d.status === 'unavailable'))
        return false;
      return POLL_INTERVAL_MS;
    },
  });

  // Restart the timeout clock when a new transaction begins OR a new step starts.
  const step = query.data?.step;
  useEffect(() => {
    if (transactionId) {
      startRef.current = Date.now();
      setTimedOut(false);
    } else {
      startRef.current = null;
    }
  }, [transactionId, step]);

  const settled =
    query.data?.status === 'completed' ||
    query.data?.status === 'error' ||
    query.data?.status === 'unavailable';

  useEffect(() => {
    if (!transactionId || timedOut || settled) return;
    const interval = setInterval(() => {
      if (startRef.current && Date.now() - startRef.current > POLL_TIMEOUT_MS) {
        setTimedOut(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [transactionId, timedOut, settled]);

  // Clear a lingering "taking longer" banner once the step settles, even if the
  // `step` string didn't change (e.g. search → search/completed) — otherwise the
  // banner renders over the next screen and the user is stuck re-clicking Retry.
  useEffect(() => {
    if (settled) setTimedOut(false);
  }, [settled]);

  return {
    draft: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    timedOut,
    refetch: query.refetch,
  };
}
